import React from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePublicInsightDetails } from '../hooks/usePublicInsightDetails';
import { useValidateShareCode } from '../hooks/useValidateShareCode';
import InsightDetailsHeader from './InsightDetailsHeader';
import InsightDetailsContent from './InsightDetailsContent';
import InsightDetailsSkeleton from './InsightDetailsSkeleton';
import ErrorScreen from '../../../core-components/theme/components/ErrorScreen';
import StatusMessageScreen from '../../../core-components/theme/components/StatusMessageScreen';
import 'react-quill/dist/quill.snow.css';

const PublicInsightDetails: React.FC = () => {
  const { t } = useTranslation();
  const { insightId = '', shareCode = '' } = useParams<{ insightId: string; shareCode?: string }>();
  const isLoggedIn = !!localStorage.getItem('userId');

  const shouldValidate = !isLoggedIn && !!shareCode;
  const { isAccessible, isValidating, validationError, message } = useValidateShareCode(shareCode, shouldValidate);

  const shouldFetchDetails = isLoggedIn || (shouldValidate && isAccessible === true);
  const { insightDetails, isInsightDetailsLoading, insightDetailsError } = usePublicInsightDetails(
    insightId,
    shouldFetchDetails && !!insightId
  );

  if (!insightId) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <ErrorScreen />
      </Box>
    );
  }

  if (shouldValidate && isValidating) {
    return <InsightDetailsSkeleton />;
  }

  if (shouldValidate && !isValidating && (validationError || isAccessible === false)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '90%' }}>
        <StatusMessageScreen
          type="negative"
          messageHeading={t('insights.public.accessDenied', 'Access Denied')}
          messageBody={message || t('insights.public.linkExpired', 'This link is no longer accessible or has expired.')}
        />
      </Box>
    );
  }

  if (isInsightDetailsLoading) {
    return <InsightDetailsSkeleton />;
  }

  if (insightDetailsError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <ErrorScreen />
      </Box>
    );
  }

  if (!insightDetails) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <ErrorScreen /> 
      </Box>
    );
  }

  return (
    <>
      <InsightDetailsHeader insightDetails={insightDetails} isPublic={true} />
      <InsightDetailsContent insightId={insightId} insightDetails={insightDetails} isPublic={true} />
    </>
  );
};

export default PublicInsightDetails;

